import React from 'react';
import { Navbar } from '../components/Navbar';
import { BentoGrid } from '../components/BentoGrid';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { Download, Briefcase, GraduationCap, Code } from 'lucide-react';
import { Link } from 'react-router-dom';
interface ExperienceItem {
  role: string;
  company: string;
  period: string;
  description: string;
  technologies: string[];
}
const experiences: ExperienceItem[] = [{
  role: 'Vice President - Product Design',
  company: 'BNY',
  period: 'Jan 2024 - Present',
  description: "I worked with a team of designers to deliver world class solutions to clients. Notably for Verizon, we overhauled internal systems for their sales and product teams. More recently I've been working with a major financial services company to redesign their internal and client facing systems",
  technologies: ['Figma', 'Gen AI', 'Design Systems']
}, {
  role: 'Senior Designer',
  company: 'Orion Inovation',
  period: 'Jun 2023 - Dec 2023',
  description: "I worked with a team of designers to deliver world class solutions to clients. Notably for Verizon, we overhauled internal systems for their sales and product teams. More recently I've been working with a major financial services company to redesign their internal and client facing systems",
  technologies: ['Figma', 'Blender', 'Design Systems', 'User Research']
}, {
  role: 'Product Designer',
  company: 'Orion Inovation',
  period: 'Apr 2022 - Jun 2023',
  description: 'Designed end-to-end experiences for fintech clients. Conducted user testing and iterated on designs based on feedback and analytics.',
  technologies: ['Sketch', 'Figma', 'Photohop', 'After Effects']
}, {
  role: 'Senior Designer',
  company: 'MARCH',
  period: 'Dec  2018 - Apr 2022',
  description: "I led the redesign and launch of MARCH's new website which generated significantly more traffic. I also redesigned and aligned the firm's graphic standards in addition to overseeing and managing design projects and client relationships.",
  technologies: ['Sketch', 'HTML/CSS', 'VRAY', '3DS Max']
}, {
  role: 'Designer',
  company: 'MARCH',
  period: 'June  2015 - Dec 2018',
  description: "I assisted in the UX design of a custom AR software platform to support new content types being created. The software was later spun off into its own project and now generates over half of MARCH's revenues.",
  technologies: ['Photoshop', 'After Effects', '3DS Max']
}];
const skills = {
  design: ['UI/UX Design', 'Design Systems', 'Prototyping', 'User Research', 'Interaction Design', 'Motion Design'],
  tools: ['Figma', 'Adobe Creative Suite', '3DS Max', 'V-Ray'],
  development: ['HTML/CSS', 'React (Basic)', 'Tailwind CSS', 'Storybook']
};
function ResumeSection() {
  const {
    ref,
    isVisible
  } = useScrollReveal(0.1);
  return <section className="py-20 px-6 relative" data-id="element-259">
      <div ref={ref} className={`max-w-7xl mx-auto transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} data-id="element-260">
        <div className="flex items-center justify-between mb-12" data-id="element-261">
          <h2 className="text-4xl md:text-5xl font-light uppercase tracking-[0.3em] text-white" data-id="element-262">
            Resume
          </h2>
          <button className="hidden md:flex items-center gap-2 px-6 py-3 bg-white/10 text-white border border-white/20 rounded-full font-medium hover:bg-white/20 transition-colors" data-id="element-263">
            <Download size={20} data-id="element-264" />
            Download PDF
          </button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12" data-id="element-265">
          {/* Left Column: Experience Timeline */}
          <div className="lg:col-span-2" data-id="element-266">
            <h3 className="flex items-center gap-3 text-2xl font-bold text-white mb-8" data-id="element-267">
              <Briefcase className="text-gray-400" data-id="element-268" />
              Experience
            </h3>
            <div className="border-l-2 border-white/10 ml-3 md:ml-4 space-y-12 pb-4" data-id="element-269">
              {experiences.map((exp, index) => <div key={index} className="relative pl-8 md:pl-12" data-id="element-270">
                  {/* Timeline Marker */}
                  <div className="absolute -left-[9px] top-6 w-4 h-4 rounded-full bg-white ring-4 ring-black" data-id="element-271" />
                  <div className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8 hover:border-white/20 transition-colors" data-id="element-272">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4" data-id="element-273">
                      <div data-id="element-274">
                        <h4 className="text-xl font-bold text-white" data-id="element-275">
                          {exp.role}
                        </h4>
                        <div className="text-gray-400 font-medium" data-id="element-276">
                          {exp.company}
                        </div>
                      </div>
                      <span className="inline-block px-3 py-1 rounded-full bg-white/10 text-xs text-gray-300 whitespace-nowrap border border-white/5 w-fit" data-id="element-277">
                        {exp.period}
                      </span>
                    </div>
                    <p className="text-gray-400 mb-6 leading-relaxed text-sm md:text-base" data-id="element-278">
                      {exp.description}
                    </p>
                    <div className="flex flex-wrap gap-2" data-id="element-279">
                      {exp.technologies.map((tech, i) => <span key={i} className="text-xs font-medium text-gray-500 px-2 py-1 bg-black/30 rounded border border-white/5" data-id="element-280">
                          {tech}
                        </span>)}
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
          {/* Right Column: Sticky Sidebar (Skills & Education) */}
          <div className="lg:col-span-1 space-y-8 lg:sticky lg:top-24 h-fit" data-id="element-281">
            {/* Skills */}
            <div className="bg-white/5 border border-white/10 rounded-3xl p-8" data-id="element-282">
              <h3 className="flex items-center gap-3 text-xl font-bold text-white mb-6" data-id="element-283">
                <Code className="text-gray-400" data-id="element-284" />
                Skills & Tools
              </h3>
              <div className="space-y-6" data-id="element-285">
                <div data-id="element-286">
                  <h4 className="text-xs text-gray-500 uppercase tracking-wider mb-3 font-semibold" data-id="element-287">
                    Design
                  </h4>
                  <div className="flex flex-wrap gap-2" data-id="element-288">
                    {skills.design.map(skill => <span key={skill} className="px-3 py-1 bg-white/5 border border-white/10 rounded-full text-xs text-gray-300 hover:bg-white/10 transition-colors" data-id="element-289">
                        {skill}
                      </span>)}
                  </div>
                </div>
                <div data-id="element-290">
                  <h4 className="text-xs text-gray-500 uppercase tracking-wider mb-3 font-semibold" data-id="element-291">
                    Tools
                  </h4>
                  <div className="flex flex-wrap gap-2" data-id="element-292">
                    {skills.tools.map(skill => <span key={skill} className="px-3 py-1 bg-white/5 border border-white/10 rounded-full text-xs text-gray-300 hover:bg-white/10 transition-colors" data-id="element-293">
                        {skill}
                      </span>)}
                  </div>
                </div>
                <div data-id="element-294">
                  <h4 className="text-xs text-gray-500 uppercase tracking-wider mb-3 font-semibold" data-id="element-295">
                    Dev
                  </h4>
                  <div className="flex flex-wrap gap-2" data-id="element-296">
                    {skills.development.map(skill => <span key={skill} className="px-3 py-1 bg-white/5 border border-white/10 rounded-full text-xs text-gray-300 hover:bg-white/10 transition-colors" data-id="element-297">
                        {skill}
                      </span>)}
                  </div>
                </div>
              </div>
            </div>
            {/* Education */}
            <div className="bg-white/5 border border-white/10 rounded-3xl p-8" data-id="element-298">
              <h3 className="flex items-center gap-3 text-xl font-bold text-white mb-6" data-id="element-299">
                <GraduationCap className="text-gray-400" data-id="element-300" />
                Education
              </h3>
              <div className="space-y-8" data-id="element-301">
                <div className="relative pl-6 border-l border-white/10" data-id="element-302">
                  <div className="absolute left-[-5px] top-1.5 w-2.5 h-2.5 rounded-full bg-white ring-4 ring-black" data-id="element-303" />
                  <div className="text-white font-bold text-lg" data-id="element-304">
                    B.ARCH Architecture
                  </div>
                  <div className="text-gray-400 text-sm mt-1" data-id="element-305">
                    Pratt Institute | School of Archtecture
                  </div>
                  <div className="text-gray-500 text-xs mt-1 font-mono" data-id="element-306">
                    2010 - 2015
                  </div>
                </div>
                <div className="relative pl-6 border-l border-white/10" data-id="element-307">
                  <div className="absolute left-[-5px] top-1.5 w-2.5 h-2.5 rounded-full bg-gray-500 ring-4 ring-black" data-id="element-308" />
                  <div className="text-white font-bold text-lg" data-id="element-309">
                    UX Design/Interaction Design
                  </div>
                  <div className="text-gray-400 text-sm mt-1" data-id="element-310">
                    Google Studies
                  </div>
                  <div className="text-gray-500 text-xs mt-1 font-mono" data-id="element-311">
                    2020
                  </div>
                </div>
              </div>
            </div>
            {/* Mobile Download Button (Visible only on mobile) */}
            <div className="md:hidden pt-4" data-id="element-312">
              <button className="flex items-center justify-center gap-2 w-full px-6 py-4 bg-white/10 text-white border border-white/20 rounded-full font-medium hover:bg-white/20 transition-colors" data-id="element-313">
                <Download size={20} data-id="element-314" />
                Download Resume
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
export function AboutPage() {
  const navLinks = [{
    label: 'Home',
    href: '/'
  }, {
    label: 'Work',
    href: '/#work'
  }, {
    label: 'About',
    href: '/aboutme'
  }];
  return <div className="min-h-screen bg-zinc-950 text-white selection:bg-white selection:text-black" data-id="element-315">
      <Navbar brand="Taylor Sams" links={navLinks} contactEmail="taylorjsams@gmail.com" data-id="element-316" />
      <main className="pt-20" data-id="element-317">
        <BentoGrid data-id="element-318" />
        <ResumeSection data-id="element-319" />
      </main>
      <footer className="py-12 text-center text-gray-600 text-sm border-t border-white/10" data-id="element-320">
        <p data-id="element-321">© {new Date().getFullYear()} Taylor Sams. All rights reserved.</p>
        <div className="mt-4" data-id="element-322">
          <Link to="/" className="text-gray-500 hover:text-white transition-colors" data-id="element-323">
            Back to Home
          </Link>
        </div>
      </footer>
    </div>;
}